import { resolveActiveAuditOverrides } from './truthAttestations';
/**
 * R-71 — dispatch-time provider coverage gate (canonical).
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { unavailableSentence, type UnavailableSectionFields } from '../evidenceUnavailableReason';
import { allowsScreeningWithSourceGaps } from '../reportGeneration/screeningPolicy';
import { PROVIDER_LABELS } from '../../config/reportRequiredProviders';
import { detectBaselineSentiment } from '../detectBaselineSentiment';
import type { SiteAuditOverride } from '../../types/site';
import type {
  EvidenceSection,
  EvidenceStatus,
  ProviderCoverageStatus,
  PublicSentimentSection,
  SedSectionId,
  SiteEvidenceDocument,
} from '../../types/siteEvidence';
import {
  getProviderCoverageSpec,
  normalizeProviderId,
} from './providerCoverageRegistry';

export type { ProviderCoverageStatus };

export interface ProviderCoverageItem {
  providerId: string;
  providerLabel: string;
  status: ProviderCoverageStatus;
  message: string;
  sedSectionId?: SedSectionId;
}

export interface ProviderCoverageReport {
  satisfied: string[];
  missingRequired: ProviderCoverageItem[];
  missingRecommended: ProviderCoverageItem[];
  satisfiedByFallback: Array<{ required: string; fallback: string }>;
  overallStatus: 'pass' | 'block' | 'warn';
  /** Back-compat for preflight UI + store. */
  passed: boolean;
  blockingFailures: ProviderCoverageItem[];
  warnings: ProviderCoverageItem[];
  retryHint: string;
  admissionBasis?: 'screening-with-source-gaps';
}

/** @deprecated Use ProviderCoverageReport */
export type ProviderCoverageResult = ProviderCoverageReport;
/** @deprecated Use ProviderCoverageItem */
export type ProviderCoverageFailure = ProviderCoverageItem;

export interface GateDecision {
  pass: boolean;
  report: ProviderCoverageReport;
  blockingItems: ProviderCoverageItem[];
}

export class ProviderCoverageError extends Error {
  readonly report: ProviderCoverageReport;

  constructor(report: ProviderCoverageReport) {
    const labels = report.missingRequired.map(f => f.providerLabel).join(', ');
    super(`Provider coverage gate blocked dispatch${labels ? `: ${labels}` : ''}`);
    this.name = 'ProviderCoverageError';
    this.report = report;
  }
}

// I (2026-08-12) — 'baseline' (classifier returned defaults: zero variance in
// a legitimately-quiet market) and 'empty' (source ran, site has no data) are
// NORMAL sentiment outcomes, not defects: they produce no dispatch block and
// no audit-ledger warning. The single aggregate signal for a fully-silent
// section remains: section.status === 'unavailable' → one warning.
// 'missing' (never attempted) stays blocking — it is actionable via re-run.
const BLOCKING_STATUSES: ReadonlySet<ProviderCoverageStatus> = new Set([
  'failed',
  'skipped',
  'unavailable',
  'missing',
]);

const RETRY_HINT = 'Retry SED bootstrap to re-fetch failed providers.';

/** Maps provider id → SED section key. Keep aligned with preflightCheck SECTION_PRIMARY_PROVIDER. */
export const PROVIDER_TO_SED_SECTION: Record<string, SedSectionId> = {
  'open-meteo': 'climate',
  'usgs-seismic': 'seismic',
  'fema-flood': 'flood',
  'hifld-grid': 'grid',
  'usfws-env': 'environmental',
  'wri-aqueduct': 'waterStress',
  'eia-electricity': 'electricity',
  'airnow-aqi': 'airQuality',
  'nrel-solar': 'solar',
  'nrel-wind': 'wind',
  'nrc-reactors': 'nuclearInfra',
  'peeringdb-ix': 'connectivity',
  'site-research': 'siteResearch',
  'regrid-parcels': 'parcel',
  'jurisdiction-boundaries': 'regulatory',
  'regulatory-db': 'regulatory',
  'usgs-waters': 'waterResource', // RD-1 (D) — refdata-only
  'external-literature': 'externalLiterature',
  'public-sentiment': 'publicSentiment',
  'sentiment-scan': 'publicSentiment',
  // PSR tier-1 — the deep sentiment fetchers have no SED sections of their
  // own; their evidence is stamped onto the publicSentiment section by the
  // orchestrator (tier1Facts / tier1SourcesAttempted). Without these
  // mappings they resolved to status 'missing' → BLOCKING, which hard-
  // blocked every public-sentiment dispatch in prod.
  'census-acs': 'publicSentiment',
  'bls': 'publicSentiment',
  'fcc': 'publicSentiment',
  'local-public-data': 'publicSentiment',
  'local-gis': 'publicSentiment',
  'news-aggregator': 'publicSentiment',
};

/** PSR tier-1 — providers resolved via the publicSentiment section's tier-1
 *  stamps rather than a dedicated SED section. Keep aligned with
 *  TIER1_SENTIMENT_FETCHER_IDS in src/services/sentimentFetchers/types.ts. */
const TIER1_SENTIMENT_PROVIDER_IDS: ReadonlySet<string> = new Set([
  'census-acs',
  'bls',
  'fcc',
  'local-public-data',
  'local-gis',
  'news-aggregator',
]);

function providerLabel(providerId: string): string {
  const normalized = normalizeProviderId(providerId);
  return PROVIDER_LABELS[providerId] ?? PROVIDER_LABELS[normalized] ?? providerId;
}

function evidenceStatusToCoverage(status: EvidenceStatus | undefined): ProviderCoverageStatus {
  if (!status) return 'missing';
  switch (status) {
    case 'unavailable':
      return 'unavailable';
    case 'anomalous':
      return 'failed';
    case 'verified':
    case 'verified-from-doc':
    case 'verified-from-context':
    case 'verified-from-report':
    case 'verified-from-research':
    case 'verified-from-dam':
    case 'verified-from-intent':
      return 'success';
    default:
      return 'success';
  }
}

function resolvePublicSentimentStatus(
  section: PublicSentimentSection | undefined,
): { status: ProviderCoverageStatus; message: string } {
  if (!section) {
    return { status: 'missing', message: 'Public sentiment section has not been collected.' };
  }
  if (section.status === 'unavailable') {
    return {
      status: 'unavailable',
      message: section.reason ?? 'All sentiment sources failed or returned no observations.',
    };
  }
  // A sentiment scan that collected nothing is reported as `status: 'partial'`
  // whether some sources FAILED or every source succeeded and the market is
  // simply quiet — the orchestrator sets 'ok' only when it also has at least one
  // entry (publicSentimentOrchestrator.ts, "'ok' iff every attempted source was
  // fully successful AND we have at least one entry"). So the status cannot tell
  // those apart, and either way it fell through to detectBaselineSentiment and
  // was labelled "Sentiment classifier returned default values", describing a
  // classifier artefact rather than the collection.
  //
  // The distinction the user needs is whether a source ERRORED, which
  // `sourceCoverage[].errors` records. The STATUS IS UNCHANGED in every case:
  // it stays 'baseline', which is non-blocking AND raises no audit-ledger row,
  // deliberately, since 2026-08-12 — a quiet site is a normal outcome and "low
  // public discourse" IS the finding. Routing this to 'unavailable' instead
  // would land it in `warnings`, which `dataConfidenceCalculator` counts as
  // `missingRecommended` at 0.05, flipping an otherwise-clean site from V to R
  // and moving the composite. A message must never move a score.
  const sentimentSourceErrors = (section.sourceCoverage ?? [])
    .flatMap((row) => (row.errors ?? []).map((error) => `${row.sourceId}: ${error}`));
  if (section.entries.length === 0 && sentimentSourceErrors.length > 0) {
    return {
      status: 'baseline',
      message: `Sentiment collection returned no observations, and ${sentimentSourceErrors.length === 1 ? 'a source failed' : `${sentimentSourceErrors.length} sources failed`} — ${sentimentSourceErrors.slice(0, 3).join('; ')}. Re-run the collection before reading this as a quiet market.`,
    };
  }
  if (section.entries.length === 0) {
    return {
      status: 'baseline',
      message: 'Every sentiment source was queried and returned no observations. Nothing failed — this site has no dated local coverage to find.',
    };
  }
  if (detectBaselineSentiment(section)) {
    return {
      status: 'baseline',
      message: 'Sentiment classifier returned default values (no meaningful variance in observations).',
    };
  }
  if (section.status === 'partial') {
    return { status: 'success', message: 'Partial sentiment coverage — some sources failed but usable data exists.' };
  }
  return { status: 'success', message: 'Public sentiment data available.' };
}

/** PSR tier-1 — resolve a tier-1 sentiment fetcher's coverage from the
 *  publicSentiment section's tier-1 stamps. The orchestrator records
 *  `tier1SourcesAttempted` (every fetcher dispatched) and `tier1Facts`
 *  (per-source normalized facts), so per-source coverage is recoverable
 *  without dedicated SED sections. These providers are `recommended`
 *  (never `required`) in the registry, so a non-success here WARNS at
 *  dispatch — it must never hard-block (never-hard-block directive). */
function resolveTier1SentimentStatus(
  providerId: string,
  section: PublicSentimentSection | undefined,
): { status: ProviderCoverageStatus; message: string } {
  if (!section) {
    return { status: 'missing', message: 'Public sentiment section has not been collected.' };
  }
  if ((section.tier1Facts ?? []).some(f => f.sourceId === providerId)) {
    return { status: 'success', message: `Tier-1 sentiment source "${providerId}" returned facts.` };
  }
  if ((section.tier1SourcesAttempted ?? []).includes(providerId)) {
    // I — attempted and came back empty: a normal outcome for sites with no
    // local coverage (the stamps cannot distinguish "no data exists" from a
    // per-source error, and warning on every quiet source buried the ledger
    // in six unactionable rows per site). Non-blocking; the aggregate
    // section-level 'unavailable' remains the single all-sources-silent signal.
    return {
      status: 'empty',
      message: `Tier-1 sentiment source "${providerId}" ran and found no data for this site — a normal outcome for quiet markets.`,
    };
  }
  return {
    status: 'missing',
    message: `Tier-1 sentiment source "${providerId}" has not been attempted — re-run SED bootstrap to collect it.`,
  };
}

/** Exported for enqueueCoverageGate, which needs the section's own
 *  attemptResult / unavailableCause to explain a refusal. */
export function readEvidenceSection(
  sed: SiteEvidenceDocument,
  sectionId: SedSectionId,
): EvidenceSection<unknown> | PublicSentimentSection | undefined {
  if (sectionId === 'publicSentiment') {
    return sed.publicSentiment;
  }
  const key = sectionId as keyof SiteEvidenceDocument;
  const value = sed[key];
  if (value && typeof value === 'object' && 'status' in value && 'retrievedAt' in value) {
    return value as EvidenceSection<unknown>;
  }
  return undefined;
}

export function resolveProviderStatusFromSed(
  providerId: string,
  sed: SiteEvidenceDocument | null | undefined,
): { status: ProviderCoverageStatus; message: string; sedSectionId?: SedSectionId } {
  const normalizedId = normalizeProviderId(providerId);
  const sectionId = PROVIDER_TO_SED_SECTION[providerId] ?? PROVIDER_TO_SED_SECTION[normalizedId];

  if (!sed) {
    return { status: 'missing', message: 'No Site Evidence Document loaded.' };
  }

  if (normalizedId === 'public-sentiment' || providerId === 'sentiment-scan') {
    const ps = resolvePublicSentimentStatus(sed.publicSentiment);
    return { ...ps, sedSectionId: 'publicSentiment' };
  }

  // PSR tier-1 — resolve from the publicSentiment section's tier-1 stamps.
  if (TIER1_SENTIMENT_PROVIDER_IDS.has(normalizedId)) {
    const tier1 = resolveTier1SentimentStatus(normalizedId, sed.publicSentiment);
    return { ...tier1, sedSectionId: 'publicSentiment' };
  }

  if (!sectionId) {
    return { status: 'missing', message: `No SED section mapping for provider ${providerId}.` };
  }

  const section = readEvidenceSection(sed, sectionId);
  if (!section) {
    return {
      status: 'missing',
      message: `Section "${sectionId}" has not been collected.`,
      sedSectionId: sectionId,
    };
  }

  const coverageStatus = evidenceStatusToCoverage((section as EvidenceSection<unknown>).status);
  // 'Provider returned no data for this location.' asserted that the provider
  // ANSWERED. It is emitted for a 429, an expired key, a provider skipped at
  // dispatch and a genuinely empty answer alike, so the Retry button is right
  // for some of them and the copy never says which. The producer stamps the
  // cause; name it.
  //
  // The STATUS MAPPING IS DELIBERATELY UNCHANGED. Re-mapping 'empty' to the
  // non-blocking 'empty' status (as the section 7 audit suggested) would stop a
  // required provider blocking on a genuinely empty answer — a looser gate, and
  // loosening is the direction that needs a human. This is the sentence only.
  const message =
    coverageStatus === 'unavailable'
      ? unavailableSentence(section as UnavailableSectionFields)
      : coverageStatus === 'failed'
        ? 'Provider data failed cross-validation checks.'
        : coverageStatus === 'missing'
          ? `Section "${sectionId}" has not been collected.`
          : 'Provider data available.';

  return { status: coverageStatus, message, sedSectionId: sectionId };
}

export interface ProviderCoverageOptions {
  /** When true, `missing` providers are not blocking — topUp/bootstrap will collect them. */
  bootstrapMode?: boolean;
}

function isSuccessStatus(status: ProviderCoverageStatus): boolean {
  return status === 'success';
}

function isBlockingStatus(status: ProviderCoverageStatus, opts?: ProviderCoverageOptions): boolean {
  if (opts?.bootstrapMode && status === 'missing') return false;
  return BLOCKING_STATUSES.has(status);
}

function toCoverageItem(
  providerId: string,
  sed: SiteEvidenceDocument | null | undefined,
): ProviderCoverageItem {
  const normalizedId = normalizeProviderId(providerId);
  const resolved = resolveProviderStatusFromSed(providerId, sed);
  return {
    providerId: normalizedId,
    providerLabel: providerLabel(providerId),
    status: resolved.status,
    message: resolved.message,
    sedSectionId: resolved.sedSectionId,
  };
}

function resolveRequiredWithFallbacks(
  providerId: string,
  sed: SiteEvidenceDocument | null | undefined,
  fallbackMap: Readonly<Record<string, readonly string[]>>,
  opts?: ProviderCoverageOptions,
): {
  item: ProviderCoverageItem;
  satisfied: boolean;
  satisfiedByFallback?: { required: string; fallback: string };
} {
  const normalizedId = normalizeProviderId(providerId);
  const primary = toCoverageItem(providerId, sed);
  if (isSuccessStatus(primary.status)) {
    return { item: primary, satisfied: true };
  }
  if (!isBlockingStatus(primary.status, opts)) {
    return { item: primary, satisfied: true };
  }

  const fallbacks = fallbackMap[normalizedId] ?? fallbackMap[providerId] ?? [];
  for (const fb of fallbacks) {
    const fbItem = toCoverageItem(fb, sed);
    if (isSuccessStatus(fbItem.status)) {
      return {
        item: primary,
        satisfied: true,
        satisfiedByFallback: { required: normalizedId, fallback: normalizeProviderId(fb) },
      };
    }
  }

  return { item: primary, satisfied: false };
}

export function checkProviderCoverage(
  reportTypeId: ReportTypeId,
  sed: SiteEvidenceDocument | null | undefined,
  opts?: ProviderCoverageOptions,
): ProviderCoverageReport {
  const spec = getProviderCoverageSpec(reportTypeId);
  const satisfied: string[] = [];
  const missingRequired: ProviderCoverageItem[] = [];
  const missingRecommended: ProviderCoverageItem[] = [];
  const satisfiedByFallback: Array<{ required: string; fallback: string }> = [];

  for (const providerId of spec.required) {
    const result = resolveRequiredWithFallbacks(providerId, sed, spec.fallback, opts);
    if (result.satisfied) {
      satisfied.push(normalizeProviderId(providerId));
      if (result.satisfiedByFallback) satisfiedByFallback.push(result.satisfiedByFallback);
    } else {
      missingRequired.push(result.item);
    }
  }

  for (const providerId of spec.recommended) {
    const item = toCoverageItem(providerId, sed);
    if (isBlockingStatus(item.status, opts)) {
      missingRecommended.push(item);
    } else {
      satisfied.push(normalizeProviderId(providerId));
    }
  }

  const blockingFailures = missingRequired;
  const warnings = missingRecommended;
  const passed = blockingFailures.length === 0;
  const overallStatus: ProviderCoverageReport['overallStatus'] = passed
    ? (warnings.length > 0 ? 'warn' : 'pass')
    : 'block';

  return {
    satisfied,
    missingRequired,
    missingRecommended,
    satisfiedByFallback,
    overallStatus,
    passed,
    blockingFailures,
    warnings,
    retryHint: RETRY_HINT,
  };
}

export function collectWaivedProviderIds(
  opts?: {
    dispatchWaivers?: { providerId: string }[];
    auditOverrides?: SiteAuditOverride[];
    reportTypeId?: ReportTypeId;
  },
): Set<string> {
  const waived = new Set<string>();
  for (const w of opts?.dispatchWaivers ?? []) {
    waived.add(normalizeProviderId(w.providerId));
  }
  for (const ov of resolveActiveAuditOverrides(opts?.auditOverrides)) {
    if ('gate' in ov && ov.gate === 'provider-coverage') {
      if (ov.reportTypeId && opts?.reportTypeId && ov.reportTypeId !== opts.reportTypeId) continue;
      for (const field of ov.fields ?? []) waived.add(normalizeProviderId(field));
    }
    if ('type' in ov && ov.type === 'coverage') {
      if (ov.reportTypeId && opts?.reportTypeId && ov.reportTypeId !== opts.reportTypeId) continue;
      for (const field of ov.fields ?? []) waived.add(normalizeProviderId(field));
    }
  }
  return waived;
}

export function dispatchCoverageGate(
  reportTypeId: ReportTypeId,
  sed: SiteEvidenceDocument | null | undefined,
  opts?: ProviderCoverageOptions & {
    dispatchWaivers?: { providerId: string }[];
    auditOverrides?: SiteAuditOverride[];
  },
): GateDecision {
  const report = checkProviderCoverage(reportTypeId, sed, opts);
  const waived = collectWaivedProviderIds({ ...opts, reportTypeId });
  const blockingItems = report.missingRequired.filter(item => !waived.has(item.providerId));

  if (blockingItems.length > 0 && allowsScreeningWithSourceGaps(reportTypeId)) {
    return { pass: true, blockingItems: [], report: {
      ...report, admissionBasis: 'screening-with-source-gaps', passed: true, overallStatus: 'warn',
      blockingFailures: [], warnings: [...report.missingRequired, ...report.warnings],
      retryHint: 'Screening uses available evidence. Unavailable coverage remains unknown; refresh when sources improve.',
    } };
  }

  if (blockingItems.length > 0) {
    const blockedReport: ProviderCoverageReport = {
      ...report,
      missingRequired: blockingItems,
      blockingFailures: blockingItems,
      passed: false,
      overallStatus: 'block',
    };
    throw new ProviderCoverageError(blockedReport);
  }

  return { pass: true, report, blockingItems: [] };
}

/** Map gate failures back to SED section ids for refreshSedSections. */
export function sedSectionIdsForCoverageFailures(
  failures: readonly ProviderCoverageItem[],
): SedSectionId[] {
  const ids = new Set<SedSectionId>();
  for (const f of failures) {
    if (f.sedSectionId) ids.add(f.sedSectionId);
  }
  return [...ids];
}

export function applyProviderCoverageDispatchGate(
  reportTypeId: ReportTypeId,
  sed: SiteEvidenceDocument | null | undefined,
  opts?: ProviderCoverageOptions & {
    dispatchWaivers?: { providerId: string }[];
    auditOverrides?: SiteAuditOverride[];
  },
): ProviderCoverageReport {
  return dispatchCoverageGate(reportTypeId, sed, opts).report;
}
